import {
    Button,
    Checkbox,
    ComboboxItem,
    ComboboxItemGroup, ComboboxPopover,
    Divider,
    Fieldset, Group,
    Select,
    Stack,
    TextInput
} from "@mantine/core";
import {ILinkCategoryWithLinks} from "@/types/models/ILinkCategory.ts";
import {useMemo} from "react";
import {useForm} from "@mantine/form";
import {ILink} from "@/types/models/ILink.ts";
import {useLocation} from "react-router-dom";
import {modals} from "@mantine/modals";
import {ADD_EDIT_FAVORITE_LINK_MODAL_ID} from "@/components/SidebarLinks/AddEditFavoriteLinkModal/modal_utils.tsx";
import {UserLinkEditable} from "@/components/SidebarLinks/ManageFavoriteLinksModal/modal.tsx";
import {randomId} from "@mantine/hooks";
import {updateManageFavoriteLinksModal} from "@/components/SidebarLinks/ManageFavoriteLinksModal/modal_utils.tsx";

enum PrefillLinkOption {
    BASE_LINK = "Prefill base Link",
    WITH_QUERY_PARAMETERS = "Prefill Link with Query Parameters"
}

export default function AddEditFavoriteLinkModal({linkData, userLink, reloadLinks, favorites}: {linkData: ILinkCategoryWithLinks[], favorites: UserLinkEditable[], reloadLinks: () => void, userLink?: UserLinkEditable}) {
    const location = useLocation()

    const linkById = useMemo(() => {
        let result: Record<string, ILink> = {}
        for (let category of linkData) {
            for (let link of category.links) {
                result[`${link.id}`] = link
            }
        }
        return result
    }, [linkData])

    const linkComboboxData = useMemo(() => {
        let comboboxData: ComboboxItemGroup<ComboboxItem>[] = []
        for (let category of linkData) {
            let comboboxCategory: ComboboxItemGroup<ComboboxItem> = {
                group: category.name,
                items: []
            }
            for (let link of category.links) {
                comboboxCategory.items.push({
                    value: `${link.id}`,
                    label: link.name
                })
            }
            comboboxData.push(comboboxCategory)
        }
        return comboboxData
    }, [linkData])

    const form = useForm<{
        omniLink: string | null
        editOmniLink: boolean
        name: string
        href: string
    }>({
        initialValues: {
            omniLink: (userLink && userLink.link_id) ? `${userLink.link_id}` : null,
            editOmniLink: false,
            name: userLink ? `${userLink.name}` : "",
            href: userLink ? `${userLink.href}` : ""
        },
        onValuesChange: (values, previous) => {
            if (values.omniLink != previous.omniLink) {
                if (values.omniLink && values.omniLink in linkById) {
                    let omniLink = linkById[values.omniLink]
                    form.setFieldValue("editOmniLink", false)
                    form.setFieldValue("name", omniLink.name)
                    form.setFieldValue("href", omniLink.href)
                } else {
                    form.setFieldValue("editOmniLink", false)
                    form.setFieldValue("name", "")
                    form.setFieldValue("href", "")
                }
            }
        }
    })

    const usableAsTemplate = !!form.getValues().omniLink && !form.getValues().omniLink?.startsWith("L")

    const showLinkPropertyFields = form.getValues().omniLink == null || (usableAsTemplate && form.getValues().editOmniLink) || form.getValues().omniLink?.startsWith("L")

    return <form onSubmit={form.onSubmit((v) => {
        let favoritesTemp = favorites.map((v) => v)
        let editable: UserLinkEditable = {
            id: userLink?.id,
            link_id: showLinkPropertyFields ? null : v.omniLink ? parseInt(v.omniLink) ?? null : null,
            name: showLinkPropertyFields ? v.name : null,
            href: showLinkPropertyFields ? v.href : null
        }

        if (editable.id) {
            for (let l of favoritesTemp) {
                if (l.id == editable.id) {
                    l.link_id = editable.link_id
                    l.name = editable.name
                    l.href = editable.href
                }
            }
        } else {
            let linkWithNewId: UserLinkEditable = {...editable, id: randomId()}
            favoritesTemp.push(linkWithNewId)
        }
        updateManageFavoriteLinksModal(linkData, reloadLinks, favoritesTemp)

        modals.close(ADD_EDIT_FAVORITE_LINK_MODAL_ID)
    })}>
        <Stack>
            <Fieldset legend={"Prefill Link"}>
                <Stack>
                    <Select searchable clearable label={"Omni Link"} description={"Pin an Omni Link to your Favorites (will update if the link is changed) or use one as a template to edit your own link"} data={linkComboboxData} {...form.getInputProps("omniLink")}/>
                    <Divider label={"or"}/>
                    <ComboboxPopover data={Object.values(PrefillLinkOption) as string[]} value={""} onChange={(v) => {
                        if (v == PrefillLinkOption.BASE_LINK) {
                            form.setFieldValue("name", document.title.split("|")[0].trim())
                            form.setFieldValue("href", location.pathname)
                        } else {
                            form.setFieldValue("name", document.title.split("|")[0].trim())
                            form.setFieldValue("href", location.pathname + location.search)
                        }
                    }}>
                        <ComboboxPopover.Target>
                            <Button fullWidth>Use current Page</Button>
                        </ComboboxPopover.Target>
                    </ComboboxPopover>
                </Stack>
            </Fieldset>
            {usableAsTemplate && <Checkbox label={"Use as Template"} {...form.getInputProps("editOmniLink", {type: "checkbox"})}/>}
            {showLinkPropertyFields && <>
                <TextInput label={"Name"} {...form.getInputProps("name")} required={showLinkPropertyFields}/>
                <TextInput label={"URL"} description={"The location this Link should direct users to, start with / to navigate to an Omni page"} {...form.getInputProps("href")} required={showLinkPropertyFields}/>
            </>}
            <Group justify={"end"}>
                <Button variant={"default"} onClick={() => modals.close(ADD_EDIT_FAVORITE_LINK_MODAL_ID)}>Cancel</Button>
                <Button type={"submit"} color={userLink ? "blue" : "green"}>{userLink ? "Save" : "Add"}</Button>
            </Group>
        </Stack>
    </form>
}